import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, BookOpen, Brain, Headphones, ClipboardCheck, Sparkles, Check, X,
  Play, Pause, Lock, Crown, BadgeCheck,
} from "lucide-react";
import {
  getTopic, getTopicQuiz, submitQuiz, generateTopicPodcast, listTopicPodcasts,
  curriculumPodcastUrl, startSession, endSession,
  listTopicDiagrams, diagramImageUrl,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "book", label: "Lesson", icon: BookOpen },
  { key: "flashcards", label: "Cards", icon: Brain },
  { key: "podcast", label: "Podcast", icon: Headphones },
  { key: "quiz", label: "Quiz", icon: ClipboardCheck },
];

function BookView({ book, diagrams }) {
  if (!book?.chapters) return <div className="font-bold text-zinc-500">No content yet.</div>;
  return (
    <article className="space-y-7">
      {diagrams?.length > 0 && (
        <section data-testid="verified-diagrams">
          <div className="flex items-center gap-2 mb-3">
            <BadgeCheck className="w-4 h-4" strokeWidth={2.5} />
            <h2 className="label-caps">Verified diagrams</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {diagrams.map((d) => (
              <figure
                key={d.id}
                className="nb-border rounded-2xl bg-white dark:bg-zinc-900 overflow-hidden"
                data-testid={`diagram-${d.id}`}
              >
                <div className="aspect-video bg-zinc-50 dark:bg-zinc-800 border-b-2 border-black dark:border-zinc-700 flex items-center justify-center">
                  <img
                    src={diagramImageUrl(d.id)}
                    alt={d.title}
                    loading="lazy"
                    className="w-full h-full object-contain"
                  />
                </div>
                <figcaption className="p-3">
                  <div className="font-display font-extrabold text-base leading-tight">{d.title}</div>
                  {d.caption && (
                    <div className="text-xs text-zinc-600 dark:text-zinc-400 font-medium mt-1 leading-snug">
                      {d.caption}
                    </div>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
      {book.summary && <p className="text-lg text-zinc-700 dark:text-zinc-300">{book.summary}</p>}
      {book.chapters.map((ch, ci) => (
        <section key={ci}>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold mb-3 inline-block bg-[#D4C4FB] dark:bg-[#B794F4] nb-border rounded-xl px-3 py-1 text-black">
            {ch.heading}
          </h2>
          <div className="space-y-4">
            {(ch.subheadings || []).map((sub, si) => (
              <div key={si}>
                <h3 className="font-display text-xl font-bold mb-2">{sub.title}</h3>
                {(sub.bullets || []).length > 0 && (
                  <ul className="list-disc pl-6 space-y-1 mb-2">
                    {sub.bullets.map((b, i) => <li key={i} className="font-medium">{b}</li>)}
                  </ul>
                )}
                {(sub.definitions || []).map((d, i) => (
                  <div key={i} className="nb-border rounded-xl bg-[#FFE066] dark:bg-[#F6E05E] p-3 mb-2 text-black">
                    <span className="font-extrabold">{d.term}:</span> <span className="font-medium">{d.definition}</span>
                  </div>
                ))}
                {(sub.key_concepts || []).map((k, i) => (
                  <div key={i} className="nb-border rounded-xl bg-[#9DFFB0] dark:bg-[#68D391] p-3 mb-2 text-black">
                    <span className="label-caps">Key</span><br />
                    <span className="font-bold">{k}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      ))}
    </article>
  );
}

function FlashcardsView({ cards }) {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  if (!cards?.length) return <div className="font-bold text-zinc-500">No cards yet.</div>;
  const c = cards[idx];
  return (
    <div>
      <div onClick={() => setFlipped((f) => !f)} className="cursor-pointer max-w-2xl mx-auto h-[340px]" style={{ perspective: "1200px" }}>
        <motion.div
          className="relative w-full h-full"
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }}
          style={{ transformStyle: "preserve-3d" }}
        >
          <div className="absolute inset-0 nb-border rounded-3xl bg-[#FFE066] flex items-center justify-center p-8 nb-shadow-xl text-black" style={{ backfaceVisibility: "hidden" }}>
            <div className="text-center">
              <div className="label-caps mb-4">Front</div>
              <div className="font-display text-2xl font-extrabold">{c.front}</div>
            </div>
          </div>
          <div className="absolute inset-0 nb-border rounded-3xl bg-[#9DFFB0] flex items-center justify-center p-8 nb-shadow-xl text-black" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
            <div className="text-center">
              <div className="label-caps mb-4">Back</div>
              <div className="font-display text-2xl font-extrabold">{c.back}</div>
            </div>
          </div>
        </motion.div>
      </div>
      <div className="flex justify-between items-center mt-6 max-w-2xl mx-auto">
        <button onClick={() => { setIdx((i) => Math.max(0, i - 1)); setFlipped(false); }} className="nb-button px-4 py-2" disabled={idx === 0}>← Prev</button>
        <div className="font-bold">{idx + 1} / {cards.length}</div>
        <button onClick={() => { setIdx((i) => Math.min(cards.length - 1, i + 1)); setFlipped(false); }} className="nb-button px-4 py-2" disabled={idx >= cards.length - 1}>Next →</button>
      </div>
    </div>
  );
}

function QuizView({ topicId }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setLoading(true);
    getTopicQuiz(topicId).then((q) => { setQuestions(q); setAnswers(new Array(q.length).fill(-1)); }).catch((e) => toast.error(e?.response?.data?.detail || "Quiz failed")).finally(() => setLoading(false));
  }, [topicId]);

  const submit = async () => {
    if (answers.some((a) => a === -1)) { toast.error("Answer every question"); return; }
    setSubmitting(true);
    try {
      const r = await submitQuiz(topicId, answers);
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="font-bold text-zinc-500">Generating quiz…</div>;
  if (!questions?.length) return <div className="font-bold text-zinc-500">No quiz yet.</div>;

  if (result) {
    return (
      <div data-testid="quiz-result" className="space-y-4">
        <div className="nb-card p-6 text-center">
          <div className="label-caps mb-2">Your score</div>
          <div className="font-display text-6xl font-black mb-2">{result.score_pct}%</div>
          <div className="text-zinc-600 dark:text-zinc-400 font-bold">{result.correct} / {result.total} correct · +{result.xp_earned} XP</div>
        </div>
        <div className="space-y-3">
          {result.review.map((r, i) => (
            <div key={i} className={`nb-card p-4 ${r.is_correct ? "bg-[#9DFFB0] dark:bg-[#68D391]" : "bg-[#FFB38E] dark:bg-[#F6AD55]"} text-black`}>
              <div className="font-bold mb-1">{questions[i].question}</div>
              <div className="text-sm font-medium">
                {r.is_correct ? "✓ Correct" : `✗ You chose: ${questions[i].options[r.chosen] ?? ","}`}
              </div>
              {!r.is_correct && (
                <div className="text-sm font-bold mt-1">Correct answer: {questions[i].options[r.correct_index]}</div>
              )}
              <div className="text-xs mt-1">{r.explanation}</div>
            </div>
          ))}
        </div>
        <button onClick={() => { setResult(null); setAnswers(new Array(questions.length).fill(-1)); }} className="nb-button px-5 py-3">Try again</button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {questions.map((q, qi) => (
        <div key={qi} className="nb-card p-5">
          <div className="font-display text-lg font-extrabold mb-3">{qi + 1}. {q.question}</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {q.options.map((opt, oi) => (
              <button
                key={oi}
                data-testid={`quiz-q${qi}-o${oi}`}
                onClick={() => setAnswers((a) => { const c = [...a]; c[qi] = oi; return c; })}
                className={`nb-border rounded-xl p-3 text-left font-bold transition-all ${
                  answers[qi] === oi ? "bg-[#D4C4FB] dark:bg-[#B794F4] nb-shadow text-black" : "bg-white dark:bg-zinc-900"
                }`}
              >
                {String.fromCharCode(65 + oi)}. {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        data-testid="quiz-submit"
        onClick={submit}
        disabled={submitting}
        className="nb-button w-full py-4 bg-[#9DFFB0] dark:bg-[#68D391]"
      >
        {submitting ? "Marking…" : "Submit quiz"}
      </button>
    </div>
  );
}

function PodcastView({ topicId, isPremium }) {
  const navigate = useNavigate();
  const [pods, setPods] = useState([]);
  const [style, setStyle] = useState("dialogue");
  const [tone, setTone] = useState("casual");
  const [generating, setGenerating] = useState(false);
  const [playing, setPlaying] = useState(null);

  const load = () => listTopicPodcasts(topicId).then(setPods).catch(() => {});
  useEffect(() => { load(); }, [topicId]);

  if (!isPremium) {
    return (
      <div className="nb-card p-8 text-center bg-[#FFE066] dark:bg-[#F6E05E] text-black">
        <Lock className="w-10 h-10 mx-auto mb-3" />
        <div className="font-display text-2xl font-extrabold mb-2">Podcasts are a Premium perk</div>
        <p className="font-medium mb-4">Listen on the bus, in the bath, anywhere. Premium unlocks AI-narrated revision podcasts.</p>
        <button onClick={() => navigate("/pricing")} className="nb-button px-5 py-3 bg-black text-white">See plans</button>
      </div>
    );
  }

  const gen = async () => {
    setGenerating(true);
    try {
      await generateTopicPodcast(topicId, style, tone);
      toast.success("Podcast ready!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="space-y-4">
      <div className="nb-card p-5 grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <label className="label-caps block mb-2">Format</label>
          <div className="flex gap-2">
            {[{v:"dialogue",l:"2-Person"},{v:"teacher",l:"Teacher"}].map((o) => (
              <button key={o.v} onClick={() => setStyle(o.v)} className={`nb-border rounded-xl px-3 py-2 font-bold text-sm flex-1 ${style===o.v?"bg-[#D4C4FB] dark:bg-[#B794F4] text-black":"bg-white dark:bg-zinc-900"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="label-caps block mb-2">Tone</label>
          <div className="flex gap-2">
            {[{v:"casual",l:"Chill"},{v:"exam",l:"Exam"},{v:"simple",l:"Simple"}].map((o) => (
              <button key={o.v} onClick={() => setTone(o.v)} className={`nb-border rounded-xl px-2 py-2 font-bold text-xs flex-1 ${tone===o.v?"bg-[#FFE066] text-black":"bg-white dark:bg-zinc-900"}`}>{o.l}</button>
            ))}
          </div>
        </div>
        <button onClick={gen} disabled={generating} data-testid="gen-topic-podcast" className="nb-button px-4 py-3 bg-[#9DFFB0] dark:bg-[#68D391] flex items-center justify-center gap-2">
          <Sparkles className="w-4 h-4" strokeWidth={3} /> {generating ? "Making…" : "Generate"}
        </button>
      </div>
      {pods.length === 0 ? (
        <div className="font-bold text-zinc-500 text-center py-6">No episodes yet, generate one above.</div>
      ) : pods.map((p) => (
        <div key={p.id} className="nb-card p-4 flex items-center gap-3">
          <button onClick={() => setPlaying(playing === p.id ? null : p.id)} className="nb-button w-12 h-12 rounded-full flex items-center justify-center bg-[#FFE066] dark:bg-[#F6E05E]">
            {playing === p.id ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-1" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display text-lg font-extrabold truncate">{p.title}</div>
            <div className="text-xs font-bold text-zinc-500">{p.style} · {p.tone} · {p.duration_lines} lines</div>
          </div>
          {playing === p.id && <audio src={curriculumPodcastUrl(p.id)} controls autoPlay className="max-w-[200px] sm:max-w-xs" onEnded={() => setPlaying(null)} />}
        </div>
      ))}
    </div>
  );
}

export default function TopicPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState(null);
  const [tab, setTab] = useState("book");
  const [error, setError] = useState(null);
  const [diagrams, setDiagrams] = useState([]);
  const sessionRef = useRef(null);

  useEffect(() => {
    getTopic(topicId)
      .then(setTopic)
      .catch((e) => {
        if (e?.response?.status === 402) {
          setError("Premium required");
          setTimeout(() => navigate("/pricing"), 1500);
        } else {
          setError(e?.response?.data?.detail || "Failed");
        }
      });
    // Fetch diagrams in parallel (silent-fail: topic without diagrams is normal).
    listTopicDiagrams(topicId).then(setDiagrams).catch(() => setDiagrams([]));
  }, [topicId, navigate]);

  // Start a study session while viewing the topic
  useEffect(() => {
    if (!topic || !user?.active_child_id) return;
    let active = true;
    startSession(topicId, "book").then((r) => {
      if (active) sessionRef.current = r.session_id;
    }).catch(() => {});
    return () => {
      active = false;
      const sid = sessionRef.current;
      if (sid) endSession(sid).catch(() => {});
    };
  }, [topic, topicId, user?.active_child_id]);

  if (error) return <div className="p-10 font-bold">{error}</div>;
  if (!topic) return <div className="p-10 font-bold">Loading…</div>;

  const isPremium = ["active", "trialing"].includes(user?.subscription_status);

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-zinc-950" data-testid="topic-page">
      <header className="nb-border border-l-0 border-r-0 border-t-0 bg-white dark:bg-zinc-900 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate("/curriculum")} className="nb-border rounded-xl px-3 py-2 font-bold text-sm flex items-center gap-2 hover:nb-shadow">
            <ArrowLeft className="w-4 h-4" /> {topic.subject}
          </button>
          <div className="text-sm font-bold text-zinc-500">Topic {topic.ordinal}</div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6 sm:p-10">
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-2">
          {topic.topic_name}
        </h1>
        <p className="text-zinc-600 dark:text-zinc-400 font-medium mb-8">{topic.summary}</p>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`topic-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`nb-border rounded-xl px-4 py-2 font-bold text-sm whitespace-nowrap flex items-center gap-2 ${
                tab === t.key ? "bg-black text-white dark:bg-white dark:text-black nb-shadow" : "bg-white dark:bg-zinc-900"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {tab === "book" && <BookView book={topic.book} diagrams={diagrams} />}
            {tab === "flashcards" && <FlashcardsView cards={topic.flashcards} />}
            {tab === "podcast" && <PodcastView topicId={topic.id} isPremium={isPremium} />}
            {tab === "quiz" && <QuizView topicId={topic.id} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
